/**
 * Mastery Week 4 (W4) — chapter-mastered 事件 detector.
 *
 * Two layers:
 *
 *   1. `buildChapterPayload` — pure function. Given a chapter id + a snapshot
 *      of the student's KP records + the chapter map entry, returns the
 *      W4 `ChapterMasteredEvent` payload (subject Chinese label, daysToMaster,
 *      weighted correctRate, etc.) OR `null` if the chapter isn't actually
 *      mastered. Side-effect free; trivially testable.
 *
 *   2. `checkChapterMastered` — thin wrapper that consults the first-time
 *      gate in localStorage, dispatches a `'chapter:mastered'` CustomEvent
 *      on `document`, and writes the gate. The DOM side-effects are kept
 *      isolated so the pure helper is the load-bearing piece.
 *
 * INVARIANTS:
 *   - 學習與遊戲分離 — NO import from src/game/. Game side subscribes via
 *     `window.addEventListener('chapter:mastered', …)` only.
 *   - First-time gate (`chapterMastered:{chapterId}:firedISO`) is NEVER
 *     cleared — see SPEC §7 (chapter demote → no re-fire to prevent spam).
 *   - Pure helper throws on no input only when the chapter id is unknown
 *     (programming error). Empty progress.studentKPs is a normal early
 *     state and returns null silently.
 */

import type { PlayerProgress, StudentKP, Subject } from '../../shared/types';
import { getMasteryLevel, type LeitnerBox } from './getMasteryLevel';
import {
  CHAPTER_KP_MAP,
  stripChapterTitlePrefix,
  type ChapterMapEntry,
} from './chapter-kp-map';

/** W4 §2 event payload schema. */
export interface ChapterMasteredEvent {
  studentId: string;
  studentName: string;
  chapterId: string;
  subject: '數學' | '國語' | '英語' | '自然' | '社會';
  chapterTitle: string;
  /** ISO-8601 UTC timestamp. */
  masteredAt: string;
  totalKpCount: number;
  daysToMaster: number;
  correctRate: number;
}

/** SPEC §2 subject mapping table. */
const SUBJECT_LABELS: Record<Subject, ChapterMasteredEvent['subject']> = {
  math: '數學',
  chinese: '國語',
  english: '英語',
  nature: '自然',
  social: '社會',
};

/** Mirror of W2 LEITNER interval table; SPEC §1 first-time gate localStorage key. */
const GATE_KEY_PREFIX = 'chapterMastered:';
const GATE_KEY_SUFFIX = ':firedISO';

/** Returns the localStorage key used as the first-time gate. */
export function gateKey(chapterId: string): string {
  return `${GATE_KEY_PREFIX}${chapterId}${GATE_KEY_SUFFIX}`;
}

/** Convert ISO 'YYYY-MM-DDT…' to epoch ms; '' / invalid → NaN. */
function parseIso(iso: string): number {
  if (!iso) return Number.NaN;
  return Date.parse(iso);
}

/** Whole calendar days between two epoch-ms timestamps (UTC, rounded). */
function diffDays(fromMs: number, toMs: number): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.max(0, Math.round((toMs - fromMs) / MS_PER_DAY));
}

/** SPEC §1: chapter mastered = every non-excluded KP is mastered AND fresh. */
function allKpsMastered(
  chapterEntry: ChapterMapEntry,
  studentKPs: StudentKP[],
  nowMs: number,
): boolean {
  const required = chapterEntry.kpRefs.filter(r => !r.excluded);
  if (required.length === 0) return false; // empty chapter never masters
  const byId = new Map(studentKPs.map(k => [k.kpId, k]));
  for (const ref of required) {
    const kp = byId.get(ref.kpId);
    if (!kp) return false;
    if (kp.totalAttempts === 0) return false;
    const correctRate = kp.totalAttempts > 0
      ? kp.correctCount / kp.totalAttempts
      : 0;
    const level = getMasteryLevel(
      {
        box: kp.box as LeitnerBox,
        attempts: kp.totalAttempts,
        correctRate,
        lastAttemptAt: parseIso(kp.lastReviewedISO) || undefined,
      },
      nowMs,
    );
    if (level !== 'mastered') return false;
  }
  return true;
}

/**
 * Pure helper: build the event payload, or null when the chapter isn't
 * mastered. Does NOT consult or touch the first-time gate — that lives
 * in `checkChapterMastered` only.
 */
export function buildChapterPayload(args: {
  chapterId: string;
  progress: PlayerProgress;
  nowMs: number;
}): ChapterMasteredEvent | null {
  const { chapterId, progress, nowMs } = args;
  const entry = CHAPTER_KP_MAP[chapterId];
  if (!entry) return null;

  const studentKPs = progress.studentKPs ?? [];
  if (studentKPs.length === 0) return null;

  if (!allKpsMastered(entry, studentKPs, nowMs)) return null;

  // Compute aggregates across the chapter's required KPs.
  const required = entry.kpRefs.filter(r => !r.excluded);
  const byId = new Map(studentKPs.map(k => [k.kpId, k]));
  let sumCorrect = 0;
  let sumAttempts = 0;
  let earliestReviewMs = Number.POSITIVE_INFINITY;
  for (const ref of required) {
    const kp = byId.get(ref.kpId);
    if (!kp) continue;
    sumCorrect += kp.correctCount;
    sumAttempts += kp.totalAttempts;
    const t = parseIso(kp.lastReviewedISO);
    // Use FIRST review observed across all chapter KPs as the start anchor.
    // We don't store a per-KP "firstSeen" so the most conservative proxy is
    // the earliest non-empty lastReviewedISO seen across the chapter.
    if (!Number.isNaN(t) && t < earliestReviewMs) earliestReviewMs = t;
  }
  const correctRate = sumAttempts > 0 ? sumCorrect / sumAttempts : 0;
  const masteredAtMs = nowMs;
  const startMs = Number.isFinite(earliestReviewMs) ? earliestReviewMs : masteredAtMs;
  const daysToMaster = diffDays(startMs, masteredAtMs);

  return {
    studentId: progress.activeProfileId ?? 'default',
    studentName: progress.studentName ?? '',
    chapterId,
    subject: SUBJECT_LABELS[entry.subject],
    chapterTitle: stripChapterTitlePrefix(entry.title),
    masteredAt: new Date(masteredAtMs).toISOString(),
    totalKpCount: required.length,
    daysToMaster,
    correctRate,
  };
}

/** Minimal storage interface — tests inject a Map-backed mock. */
export interface GateStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Minimal event dispatcher interface — tests inject a spy. */
export interface EventDispatcher {
  dispatchEvent(ev: Event): boolean;
}

/** Default storage shim — wraps globalThis.localStorage, falls back to no-op. */
function defaultStorage(): GateStorage {
  try {
    const ls = (globalThis as { localStorage?: GateStorage }).localStorage;
    if (ls && typeof ls.getItem === 'function' && typeof ls.setItem === 'function') {
      return ls;
    }
  } catch { /* SSR / no-DOM */ }
  return {
    getItem: () => null,
    setItem: () => { /* no-op */ },
  };
}

/** Default dispatcher — `document`. */
function defaultDispatcher(): EventDispatcher | null {
  try {
    const doc = (globalThis as { document?: EventDispatcher }).document;
    if (doc && typeof doc.dispatchEvent === 'function') return doc;
  } catch { /* SSR / no-DOM */ }
  return null;
}

/**
 * SPEC §3: orchestration. Pure helper + gate + dispatch. Returns the
 * dispatched payload (for tests / observability) or null when skipped.
 *
 * Skip reasons (silent return null):
 *   - chapter not mastered yet
 *   - empty studentKPs
 *   - first-time gate already set
 *   - no document available (SSR)
 */
export function checkChapterMastered(
  chapterId: string,
  progress: PlayerProgress,
  options: {
    nowMs?: number;
    storage?: GateStorage;
    dispatcher?: EventDispatcher | null;
  } = {},
): ChapterMasteredEvent | null {
  const nowMs = options.nowMs ?? Date.now();
  const storage = options.storage ?? defaultStorage();
  const dispatcher = options.dispatcher !== undefined
    ? options.dispatcher
    : defaultDispatcher();

  // First-time gate — peek BEFORE running the expensive aggregate.
  const key = gateKey(chapterId);
  if (storage.getItem(key)) return null;

  const payload = buildChapterPayload({ chapterId, progress, nowMs });
  if (!payload) return null;

  // SSR / headless — no document to dispatch to. Skip BUT still write the
  // gate to avoid surprise re-fire on next page load? No — without the
  // event reaching subscribers, downstream banners / LINE push wouldn't
  // happen. Safer to leave the gate open so a real DOM context can fire.
  if (!dispatcher) return null;

  const ev = new CustomEvent<ChapterMasteredEvent>('chapter:mastered', {
    detail: payload,
    bubbles: true,
    cancelable: false,
  });
  try {
    dispatcher.dispatchEvent(ev);
  } catch {
    return null;
  }

  // Write gate ONLY after successful dispatch.
  try {
    storage.setItem(key, payload.masteredAt);
  } catch { /* localStorage quota / private mode — fire-and-forget */ }

  return payload;
}
